import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import MemberBalances from '../components/MemberBalances';
import { getDefaultDropdownMenu, getLoyaltyDropdownMenu } from '../utils/getDropdownMenu';
import { useLocation } from 'react-router-dom';
import StandardButton from '../components/StandardButton';
import combineStyles from '../utils/combineStyles';
import history from '../history';
import getThemeLookup from '../selectors/getThemeLookup';
import { logEvent, orderNow } from '../utils/analytics';
import EVENTS from '../constants/events';
import { signOut } from '../thunks/signOut';
import { OrderingOperations, OrderingSelectors } from 'polygon-ordering';
import { setCurrentModal } from '../thunks/setCurrentModal';
import { SIGN_IN_MODAL_ID } from '../modals/SignInModal';
import determineCalloutOffsets from '../utils/determineCalloutOffsets';
import Text from '../components/Text';
import { setCurrentDrawer } from '../slices/currentDrawer';
import Drawer from './Drawer';
import { removeUserSession } from '../utils/analytics';
import { LOYALTY_MIGRATION_ROUTE } from '../screens/LoyaltyMigrationScreen';
import navigateToHomeScreen from '../utils/navigateToHomeScreen';
import { setUserLogout } from '../slices/userLogout';
import { REGISTRATION_VERIFY } from '../screens/RegistrationVerify';
import { RiArrowRightSLine } from 'react-icons/ri';
import TouchableOpacity from '../components/TouchableOpacity';
import { CONTAINER_PROPERTIES } from '../utils/theme';
import isNestedRouteActive from '../utils/isNestedRouteActive';
import { useRedcatAnalytics, trackSigninStarted } from '../analytics';

export const LEFT_MENU_DRAWER = 'LEFT_MENU_DRAWER';

const { updateSelectedPaymentMethods, setAgeProofSubmitted, setUserUnderage } = OrderingOperations;
const { getMember, getLocation, getNonVerifiedMemberPresent } = OrderingSelectors;

const LeftMenuDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { track } = useRedcatAnalytics();
  const { t } = useTranslation();
  const member = useAppSelector(getMember);
  const currentDrawer = useAppSelector(state => state.currentDrawer);
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  const loyaltyDisabled = useAppSelector(state => state.config.disableLoyalty);
  const selfRef = React.useRef<HTMLDivElement>(null);
  const enableLoyaltyMigration = useAppSelector(state => state.config.enableLoyaltyMigration);
  const isOnlyLoyaltyEnabled = useAppSelector(state => state.config).enableOnlyLoyalty;
  const location = useAppSelector(getLocation);
  const isUnverified = useAppSelector(getNonVerifiedMemberPresent);

  const getMenus = () => {
    if (!member) {
      return getDefaultDropdownMenu();
    }

    if (loyaltyDisabled) {
      return getDefaultDropdownMenu();
    }

    return getLoyaltyDropdownMenu();
  };

  const menus = getMenus();

  const p = useAppSelector(getThemeLookup);

  const { pathname } = useLocation();

  return (
    <Drawer
      position="left"
      open={currentDrawer === LEFT_MENU_DRAWER ? true : false}
      closeButtonPosition={deviceTypeMobile ? 'left' : 'right'}
    >
      {member && (
        <div style={{ paddingLeft: '2.25em' }}>
          <Text style={{ fontSize: 14, fontWeight: 'bold' }}>
            {member?.givenName + ' ' + member?.surname}
          </Text>
        </div>
      )}
      {member && isUnverified && (
        <TouchableOpacity
          style={{
            ...styles.verificationBanner,
            backgroundColor: p('verificationBanner', ['backgroundColor']).backgroundColor,
          }}
          onClick={() => {
            dispatch(setCurrentDrawer(null));
            history.push(REGISTRATION_VERIFY);
          }}
        >
          <div style={styles.verificationBannerContent}>
            <Text
              style={{
                ...styles.verificationBannerTitle,
                color: p('verificationBannerText', ['color']).color,
              }}
            >
              {t('RegistrationVerify.bannerTitle')}
            </Text>
            <Text
              style={{
                ...styles.verificationBannerBody,
                color: p('verificationBannerText', ['color']).color,
              }}
            >
              {t('RegistrationVerify.bannerBody')}
            </Text>
          </div>
          <RiArrowRightSLine
            style={{
              ...styles.verificationBannerArrow,
              color: p('verificationBannerText', ['color']).color,
            }}
          />
        </TouchableOpacity>
      )}

      {member && (
        <div style={{ padding: '0 calc(2.25em - 15px) 0.25em' }}>
          <MemberBalances />
        </div>
      )}

      {enableLoyaltyMigration && !member && (
        <StandardButton
          containerStyle={styles.button}
          themeKey="loyaltyMigrationMenuButton"
          label={t('leftMenu.buttons.loyaltyMigration')}
          onClick={() => {
            dispatch(setCurrentDrawer(null));
            history.push(LOYALTY_MIGRATION_ROUTE);
          }}
        />
      )}

      {!isOnlyLoyaltyEnabled && (
        <StandardButton
          containerStyle={combineStyles(styles.button)}
          themeKey="orderOnlineButton"
          label={t('button.orderOnline')}
          onClick={async () => {
            dispatch(setCurrentDrawer(null));
            orderNow({ store: location?.name, origin: 'Navigation' });
            navigateToHomeScreen(pathname, dispatch);
          }}
        />
      )}
      {!member && (
        <StandardButton //login button
          ref={selfRef}
          invisibleContainer
          onClick={() => {
            dispatch(setCurrentDrawer(null));
            trackSigninStarted(track, { entryPoint: 'nav_bar' });
            dispatch(
              setCurrentModal({
                modalId: SIGN_IN_MODAL_ID,
                calloutOffsets: determineCalloutOffsets(
                  (
                    selfRef as React.MutableRefObject<HTMLDivElement>
                  ).current.getBoundingClientRect(),
                ),
              }),
            );
          }}
          label={t('button.headerMemberAbsent')}
          themeKey="defaultDropdownMenuButton"
          containerStyle={combineStyles(
            styles.button,
            deviceTypeMobile ? styles.buttonMobile : styles.buttonDesktop,
          )}
        />
      )}

      {menus.map((screen, index) => {
        const isScreenActive =
          isNestedRouteActive(pathname, screen.path) ||
          !!screen.subRoutes?.some(route => isNestedRouteActive(pathname, route.path));

        return (
          <div key={index}>
            <StandardButton
              label={t(screen.label)}
              themeKey={isScreenActive ? 'activeMemberButton' : 'defaultDropdownMenuButton'}
              containerStyle={combineStyles(
                styles.button,
                deviceTypeMobile ? styles.buttonMobile : styles.buttonDesktop,
                isScreenActive && p('activeMemberButton', CONTAINER_PROPERTIES),
              )}
              onClick={() => {
                history.push(screen.path);
                if (!screen.subRoutes) {
                  dispatch(setCurrentDrawer(null));
                }
              }}
            />
            {screen.subRoutes != undefined &&
              screen.subRoutes.some(route => isNestedRouteActive(pathname, route.path)) && (
                <div style={styles.subRouteWrapper}>
                  {screen.subRoutes?.map(route => (
                    <div key={route.label}>
                      <StandardButton
                        key={index}
                        label={t(route.label)}
                        themeKey={
                          isNestedRouteActive(pathname, route.path)
                            ? 'activeSubButton'
                            : 'defaultSubButton'
                        }
                        containerStyle={combineStyles(
                          styles.button,
                          styles.subRouteButton,
                          deviceTypeMobile ? styles.buttonMobile : styles.buttonDesktop,
                        )}
                        onClick={() => {
                          history.push(route.path);
                          dispatch(setCurrentDrawer(null));
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
          </div>
        );
      })}
      {member && (
        <div>
          <StandardButton
            invisibleContainer
            containerStyle={combineStyles(
              deviceTypeMobile ? styles.buttonMobile : styles.buttonDesktop,
              styles.button,
            )}
            label={t('button.signOut')}
            onClick={() => {
              if (window.confirm(t('confirm.signOut'))) {
                dispatch(setUserLogout(true));
                dispatch(setAgeProofSubmitted(false));
                dispatch(setUserUnderage(false));
                logEvent(EVENTS.SIGN_OUT);
                dispatch(signOut());
                removeUserSession();
                history.push('/');
                dispatch(updateSelectedPaymentMethods({ clearPaymentMethods: true }));
                dispatch(setCurrentDrawer(null));
              }
            }}
            themeKey="defaultDropdownMenuButton"
          />
        </div>
      )}
    </Drawer>
  );
};

export default LeftMenuDrawer;

const styles: Styles = {
  button: {
    textTransform: 'uppercase',
    justifyContent: 'left',
    margin: 0,
    padding: '0.7em 2.25em',
  },

  subRouteButton: {
    padding: '0 2.25em 0 3.25em',
  },

  subRouteWrapper: {
    gap: '0.75em',
    // display: 'flex',
    flexDirection: 'column',
    paddingBottom: '1em',
  },

  verificationBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '0.5em 0',
    padding: '0.75em 1.5em',
    borderRadius: 0,
    cursor: 'pointer',
  },

  verificationBannerContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
  },

  verificationBannerTitle: {
    fontWeight: 700,
    fontSize: 13,
  },

  verificationBannerBody: {
    fontSize: 12,
    opacity: 0.8,
  },

  verificationBannerArrow: {
    fontSize: 20,
    flexShrink: 0,
  },
};
